'use client';
import React from 'react';
import { CategoryMegaMenu as SharedCategoryMegaMenu } from '@launchstore/shared-puck';
import type { CategoryMegaMenuProps, SharedMegaMenuItem, SharedCategoryMegaMenuConfig } from '@launchstore/shared-puck';
import type { EnrichedMenuItem } from '@/lib/menu/types';
import type { Theme } from '@/lib/theme/api';

/**
 * Storefront wrapper for CategoryMegaMenu.
 *
 * WHY THIS SHAPE
 * The shared `CategoryMegaMenu` is a plain `React.FC` (a sub-renderer
 * documented as "NOT a Puck component"), so it cannot be spread into a
 * config. We build a fresh `ComponentConfig` whose `render` delegates to
 * the shared FC via `React.createElement`. See MenuNavigationRenderer.tsx
 * for the same pattern and rationale.
 *
 * Registering the previous version (a bare `React.FC`) in the Puck config
 * crashed pages with "React error #130: element type is invalid".
 *
 * DATA: the (main) layout server-injects `item` (a single
 * EnrichedMenuItem, the first top-level menu item) and `theme` into this
 * component's puckData node via `injectMenuDataIntoTemplate` in layout.tsx.
 */
const projectItem = (item: EnrichedMenuItem): SharedMegaMenuItem => ({
  id: item.id,
  label: item.label,
  url: item.enrichedData?.resolvedUrl || item.url || (item.entityId ? `/${item.type}s/${item.entityId}` : '#'),
  openInNewTab: item.openInNewTab,
  enrichedData: {
    category: item.enrichedData?.category ? {
      id: item.enrichedData.category.id,
      handle: item.enrichedData.category.handle,
      name: item.enrichedData.category.name,
      description: item.enrichedData.category.description,
      subcategories: item.enrichedData.category.subcategories,
    } : undefined,
    resolvedUrl: item.enrichedData?.resolvedUrl,
  },
});

const projectMegaMenuConfig = (cfg: any): SharedCategoryMegaMenuConfig => ({
  enabled: cfg?.enabled ?? false,
  columns: cfg?.columns,
  showImage: cfg?.showImage,
  imageUrl: cfg?.imageUrl,
  subcategoryLimit: cfg?.subcategoryLimit,
  showDescriptions: cfg?.showDescriptions,
  showSubcategories: cfg?.showSubcategories,
});

const projectTheme = (theme: Theme) => {
  const tokens = (theme?.globalSettings?.colors?.tokens || {}) as any;
  const megaMenuTokens = tokens.megaMenu || {};
  const typography = (theme?.globalSettings?.typography || {}) as any;
  const spacing = (theme?.globalSettings as any)?.spacing?.megaMenu || {};
  const effects = (theme?.globalSettings as any)?.effects || {};
  return {
    background: megaMenuTokens.background,
    sectionHeading: megaMenuTokens.sectionHeading,
    linkText: megaMenuTokens.linkText,
    linkHover: megaMenuTokens.linkHover,
    headingFontSize: typography.fontSize?.megaMenuHeading,
    headingFontWeight: typography.fontWeight?.megaMenuHeading,
    linkFontSize: typography.fontSize?.megaMenuLink,
    linkFontWeight: typography.fontWeight?.megaMenuLink,
    padding: spacing.padding,
    columnGap: spacing.columnGap,
    borderRadius: effects.borderRadius?.megaMenu,
    boxShadow: effects.shadow?.megaMenu,
  };
};

export interface StorefrontCategoryMegaMenuProps {
  item: EnrichedMenuItem;
  theme: Theme;
  onLinkClick?: () => void;
}

const CategoryMegaMenuImpl: React.FC<StorefrontCategoryMegaMenuProps> = ({ item, theme, onLinkClick }) => {
  if (!item) return null;
  const projectedItem = projectItem(item);
  const projectedMegaMenu = projectMegaMenuConfig((item as any).megaMenu);
  const projectedTheme = theme ? projectTheme(theme) : undefined;
  // Shared export is a React.FC (sub-renderer). createElement + cast avoids
  // the dual-@types/react JSX mismatch.
  return React.createElement(SharedCategoryMegaMenu as any, {
    item: projectedItem,
    megaMenu: projectedMegaMenu,
    theme: projectedTheme,
    onLinkClick,
  } as CategoryMegaMenuProps);
};

export const CategoryMegaMenu: any = {
  label: 'Category Mega Menu',
  render: (props: any) => <CategoryMegaMenuImpl {...props} />,
};
export default CategoryMegaMenu;
